import { useEffect, useState } from "react";
import { Reveal, MaskLineInView } from "./Reveal";
import { api, API } from "@/lib/apiClient";
import { ytId } from "./WeeklyMessage";
import { GraduationCap, BookOpen, Film, ExternalLink, Download, PlayCircle } from "lucide-react";

const KINDS = [
  { key: "all", label: "All" },
  { key: "class", label: "Classes" },
  { key: "book", label: "Books" },
  { key: "video", label: "Videos" },
];

const kindIcon = { class: GraduationCap, book: BookOpen, video: Film };

function ResourceCard({ r }) {
  const fileUrl = r.file_path ? `${API}/resources/file/${r.id}` : null;
  const vid = r.youtube_url ? ytId(r.youtube_url) : null;
  const isImage = r.file_content_type?.startsWith("image/");
  const isAudio = r.file_content_type?.startsWith("audio/");
  const isVideoFile = r.file_content_type?.startsWith("video/");
  const Icon = kindIcon[r.kind] || BookOpen;

  return (
    <div className="glass rounded-3xl p-4 flex flex-col" data-testid={`resource-card-${r.id}`}>
      {(vid || isImage || isAudio || isVideoFile) && (
        <div className="relative w-full overflow-hidden rounded-2xl bg-black/40 mb-4" style={{ paddingBottom: "62%" }}>
          {vid && (
            <iframe
              src={`https://www.youtube.com/embed/${vid}`}
              title={r.title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          {isImage && <img src={fileUrl} alt={r.title} className="absolute inset-0 w-full h-full object-contain" />}
          {isVideoFile && <video controls src={fileUrl} className="absolute inset-0 w-full h-full" />}
          {isAudio && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
              <PlayCircle className="w-9 h-9 text-gold" strokeWidth={1.2} />
              <audio controls src={fileUrl} className="w-full" />
            </div>
          )}
        </div>
      )}
      <div className="px-1 flex-1 flex flex-col">
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-gold flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" /> {r.kind}
        </p>
        <p className="text-white font-display text-lg leading-snug mt-2">{r.title}</p>
        {r.note && <p className="text-xs text-[#B4ADC1] font-light mt-1.5 leading-relaxed">{r.note}</p>}
        <div className="mt-auto pt-4 flex flex-wrap gap-2">
          {r.link_url && (
            <a href={r.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs text-white hover:text-gold transition-colors" data-testid={`resource-open-${r.id}`}>
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
          )}
          {fileUrl && !isImage && !isAudio && !isVideoFile && (
            <a href={fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-black text-xs font-medium hover:bg-[#e6c14e] transition-colors" data-testid={`resource-download-${r.id}`}>
              <Download className="w-3.5 h-3.5" /> Read / Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Library() {
  const [resources, setResources] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/resources").then(({ data }) => setResources(data)).catch(() => {});
  }, []);

  if (!resources.length) return null;
  const shown = filter === "all" ? resources : resources.filter((r) => r.kind === filter);

  return (
    <section id="library" className="relative py-28 md:py-40 bg-[#05030A]">
      <div className="absolute -left-32 top-24 w-[480px] h-[380px] rounded-full bg-[#6A0DAD]/12 blur-[140px]" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><span className="section-label">The Library — Classes, Books & Videos</span></Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] mt-5 text-white">
            <MaskLineInView>Grow in the</MaskLineInView>
            <MaskLineInView delay={0.1}><span className="italic text-gold">Word.</span></MaskLineInView>
          </h2>
        </div>

        <Reveal delay={0.15}>
          <div className="flex flex-wrap justify-center gap-2 mt-10" data-testid="library-filters">
            {KINDS.map((k) => (
              <button
                key={k.key}
                onClick={() => setFilter(k.key)}
                className={`px-5 py-2.5 rounded-full text-sm transition-colors ${filter === k.key ? "bg-gold text-black" : "glass text-[#B4ADC1] hover:text-white"}`}
                data-testid={`library-filter-${k.key}`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="library-grid">
            {shown.map((r) => <ResourceCard key={r.id} r={r} />)}
          </div>
          {!shown.length && <p className="text-center text-[#7A708A] font-mono text-sm mt-10">Nothing here yet — check back soon.</p>}
        </Reveal>
      </div>
    </section>
  );
}
